import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import memo from 'memo-decorator';
import {Observable} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {Router} from '@angular/router';

import {TerraUtils} from '../../../../shared/TerraUtils';
import InvestmentSecurityType from '../../../../shared/enums/InvestmentSecurityType.enum';
import InvestmentType from 'src/app/shared/enums/InvestmentType.enum';
import HoldingType from 'src/app/shared/enums/HoldingType.enum';
import {HoldingDiscriminator} from '../../../../shared/enums/HoldingDiscriminator.enum';
import {LpAssetReqRes} from '../../../assets/LpAssetReqRes.model';
import {LpFundReqRes} from '../../../funds/LpFundReqRes.model';
import FundInvestmentStrategyType from '../../../../shared/enums/FundInvestmentStrategyType.enum';
import HoldingStatus from 'src/app/shared/enums/HoldingStatus.enum';
import {RoutingService} from '../../../../services/routing.service';
import {LocationDetailsResponse} from '../../../../shared/models/LocationDetailsResponse.model';
import {ResourceService} from '../../../../services/resource.service';
import {CountryCodePipe} from '../../../../shared/pipes/countryCode.pipe';
import {LpPaymentRequestService} from '../../../lp-top-notification/lp-payment-request.service';
import {ImpersonationService} from '../../../../services/shared/impersonation.service';
import {InvestingEntityShareService} from '../../../investing-entity-share/services/investing-entity-share.service';


@Component({
  selector: 'terra-holding-general-information',
  templateUrl: './holding-general-information.component.html',
  styleUrls: ['./holding-general-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HoldingGeneralInformationComponent implements OnInit {
  @Input() holdingDiscriminator: HoldingDiscriminator;
  @Input() holding: LpAssetReqRes | LpFundReqRes;

  EXAMPLE_ASSET_ID = TerraUtils.consts.EXAMPLE_ASSET_ID;
  isFund: boolean;
  isAsset: boolean;
  holdingAsAsset: LpAssetReqRes;
  holdingAsFund: LpFundReqRes;
  hasPendingCapitalCall$: Observable<boolean>;
  myInvestingEntityIds: number[];

  // enums
  HoldingType = HoldingType;
  SecurityType = InvestmentSecurityType;
  InvestmentType = InvestmentType;
  FundInvestmentStrategyType = FundInvestmentStrategyType;
  HoldingStatus = HoldingStatus;

  investedInHolding$ =
    this.investingEntityShareService.investingEntities$.pipe(
      map(myIe => !!(myIe?.map(ie => ie.id).filter(id => this.holding.investingEntitiesIds?.includes(id)).length)),
      shareReplay(1)
    );

  constructor(private routingService: RoutingService,
              private resourceService: ResourceService,
              private countryCodePipe: CountryCodePipe,
              private paymentRequestService: LpPaymentRequestService,
              private router: Router,
              private impersonationService: ImpersonationService,
              private investingEntityShareService: InvestingEntityShareService
  ) {
  }

  ngOnInit(): void {
    this.isAsset = this.holdingDiscriminator === HoldingDiscriminator.Asset;
    this.isFund = this.holdingDiscriminator === HoldingDiscriminator.Fund;
    this.holdingAsAsset = this.holding as LpAssetReqRes;
    this.holdingAsFund = this.holding as LpFundReqRes;
    this.hasPendingCapitalCall$ = this.paymentRequestService.pendingPaymentRequests$.pipe(
      map(paymentRequests => paymentRequests.some(paymentRequest => paymentRequest.holdingId === this.holding.id))
    );
  }

  get isViewingAsInvestor(): boolean {
    return this.impersonationService.isViewingAsInvestor;
  }

  getBankAccountEditPage(holdingId: number) {
    // return this.routingService.holdingBankAccountPreferenceEditor(holdingId);
    return this.routingService.lpBankAccounts;
  }

  navigateToCapitalCallTab(holdingId: number) {
    if (window.location.pathname === this.routingService.lpHoldingPageWithTab(holdingId, this.holdingDiscriminator, 'capital-call')) {
      this.paymentRequestService.scrollToPaymentRequest();
    }

    this.router.navigateByUrl(this.routingService.lpHoldingPageWithTab(holdingId, this.holdingDiscriminator, 'capital-call'));
  }

  @memo()
  getFormattedAddress(location: LocationDetailsResponse) {
    if (!location) {
      return '';
    }
    let formattedString = '';
    if (location.cityName) {
      formattedString += location.cityName;
      if (location.stateName || location.postalCode) {
        formattedString += ', ';
      }
    }
    if (location.stateName) {
      formattedString += location.stateName;
    }
    if (location.postalCode) {
      formattedString += ' ' + location.postalCode;
    }
    const country = this.resourceService.getCountryById(location.countryId);

    if (country && country.code) {
      if (!formattedString) {
        formattedString += country.name;
      } else {
        formattedString += ', ' + this.countryCodePipe.transform(country.code);
      }
    }

    return formattedString;
  }
}
